import Button from '@/common/components/atom/button';
import Text from '@/common/components/atom/text';
import TextField from '@/common/components/atom/textField';
import { FormEvent, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSendDownloadLink } from '../apis/sendDownloadLink';
import { APP_ARTICLE_BODY, APP_ARTICLE_TITLE, APP_FEATURES } from '../constants/content';
import { TOP_CENTERS, TOP_SPECIALTIES } from '../constants/links';
import { AppPageLink } from '../constants/links';
import { APP_STORES, CELL_PATTERN } from '../constants/stores';

const toLatinDigits = (value: string) =>
  value.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

const StoreButtons = () => (
  <div className="flex flex-col gap-3">
    {APP_STORES.map(store => (
      <a
        key={store.id}
        href={store.href}
        target="_blank"
        rel="noopener noreferrer"
        data-store={store.id}
        className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition hover:border-primary"
      >
        <img src={store.icon} alt="" width={32} height={32} className="h-8 w-8 shrink-0" />
        <Text fontSize="sm" fontWeight="semiBold" className="text-slate-800">
          {store.label}
        </Text>
      </a>
    ))}
  </div>
);

const SmsLinkForm = () => {
  const [cell, setCell] = useState('');
  const [error, setError] = useState(false);
  const sendLink = useSendDownloadLink();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const normalized = toLatinDigits(cell.trim());
    if (!CELL_PATTERN.test(normalized)) {
      setError(true);
      return;
    }
    setError(false);
    sendLink.mutate(normalized, {
      onSuccess: () => {
        toast.success('لینک دانلود اپلیکیشن به شماره‌ی شما پیامک شد.');
        setCell('');
      },
      onError: () => toast.error('ارسال پیامک انجام نشد. لطفا دوباره تلاش کنید.'),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
      <TextField
        label="لینک دانلود را پیامک کنیم"
        placeholder="مثلا ۰۹۱۲۳۴۵۶۷۸۹"
        inputMode="numeric"
        autoComplete="tel"
        dir="ltr"
        value={cell}
        onChange={e => setCell(e.target.value)}
        error={error}
        helperText={error ? 'شماره موبایل معتبر نیست.' : undefined}
      />
      <Button type="submit" block loading={sendLink.isLoading}>
        ارسال لینک
      </Button>
    </form>
  );
};

const FeatureList = () => (
  <section className="flex flex-col gap-4">
    <Text as="h2" fontSize="lg" fontWeight="bold" className="leading-7 text-slate-900">
      نزدیک‌ترین پزشک، گوشی شماست
    </Text>
    {APP_FEATURES.map(feature => (
      <div key={feature.title} className="rounded-xl border border-slate-200 bg-white p-4">
        <Text as="h3" fontSize="sm" fontWeight="semiBold" className="leading-6 text-slate-900">
          {feature.title}
        </Text>
        <Text as="p" fontSize="sm" className="mt-1 leading-6 text-slate-600">
          {feature.description}
        </Text>
      </div>
    ))}
  </section>
);

const LinkList = ({ title, links }: { title: string; links: AppPageLink[] }) => (
  <section className="flex flex-col gap-3">
    <Text as="h2" fontSize="lg" fontWeight="bold" className="leading-7 text-slate-900">
      {title}
    </Text>
    <ul className="flex flex-wrap gap-2">
      {links.map(link => (
        <li key={link.href}>
          <a
            href={link.href}
            className="inline-block rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs leading-5 text-slate-700 transition hover:border-primary hover:text-primary"
          >
            {link.title}
          </a>
        </li>
      ))}
    </ul>
  </section>
);

const Article = () => (
  <section className="flex flex-col gap-2">
    <Text as="h2" fontSize="lg" fontWeight="bold" className="leading-7 text-slate-900">
      {APP_ARTICLE_TITLE}
    </Text>
    <Text as="p" fontSize="sm" className="leading-7 text-slate-600">
      {APP_ARTICLE_BODY}
    </Text>
  </section>
);

const AppDownload = () => (
  <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 py-6" dir="rtl">
    <div className="flex flex-col items-center gap-3 text-center">
      <img src="/img/app-download/logopzdl.png" alt="پذیرش۲۴" width={72} height={72} className="h-[72px] w-[72px]" />
      <Text as="h1" fontSize="xl" fontWeight="bold" className="leading-8 text-slate-900">
        دانلود اپلیکیشن نوبت‌دهی پزشکان پذیرش۲۴
      </Text>
      <Text as="p" fontSize="sm" className="leading-6 text-slate-600">
        برای دریافت نوبت حضوری یا ویزیت آنلاین از بهترین پزشکان و مراکز درمانی ایران، اپلیکیشن رایگان پذیرش۲۴ را از یکی از فروشگاه‌های زیر نصب کنید.
      </Text>
    </div>

    <StoreButtons />

    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <SmsLinkForm />
    </div>

    <FeatureList />

    <LinkList title="مراکز درمانی برتر پذیرش۲۴" links={TOP_CENTERS} />

    <LinkList title="دریافت نوبت حضوری از بهترین پزشکان ایران" links={TOP_SPECIALTIES} />

    <Article />

    <Text as="p" fontSize="sm" className="text-center leading-6 text-slate-600">
      دنبال ویزیت آنلاین هستید؟{' '}
      <a href="/s/?turn_type=consult" className="font-semibold text-primary">
        پزشکانِ ویزیت آنلاین را ببینید
      </a>
    </Text>
  </div>
);

export default AppDownload;
